import { SchedulerCompanyTime } from '@components/scheduler/companyTime';
import { TimeInput } from '@components/timeInput';
import { FormField } from '@components/ui/form';
import { DatePicker } from '@components/ui/form/date';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import { useTranslation } from 'react-i18next';
import { getHours, getMinutes, set } from 'date-fns';
import { toTenantDate } from '@utils/timeZone';
import { InternalScheduleDateInputsProps } from './types';
import { useFormContext } from 'react-hook-form';
import { useRescheduler } from '@contexts/rescheduler';

export const InternalScheduleDateInputs = ({
  data,
  disabled,
  children,
}: InternalScheduleDateInputsProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'forms' });
  const rescheduler = useRescheduler();

  const { control, setValue, watch } = useFormContext<{
    startsAt?: string;
    endsAt?: string;
  }>();

  const { startsAt, endsAt } = watch();

  const parseValue = (value?: unknown) => {
    return toTenantDate((value || '') as string);
  };

  const setStartAndEndDateValue = (newDateValue: Date) => {
    const initialStartsAt = toTenantDate(startsAt || '');
    const updatedStartsAt = set(newDateValue, {
      hours: getHours(initialStartsAt),
      minutes: getMinutes(initialStartsAt),
    });

    const initialEndsAt = toTenantDate(endsAt || '');
    const updatedEndsAt = set(newDateValue, {
      hours: getHours(initialEndsAt),
      minutes: getMinutes(initialEndsAt),
    });

    setValue('startsAt', updatedStartsAt.toISOString());
    setValue('endsAt', updatedEndsAt.toISOString());
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-4">
        <FormField
          control={control}
          name="startsAt"
          render={({ field: { value, onChange, ...field } }) => (
            <FormItemWrapper className="w-fit" label={t('date.label')} required>
              <DatePicker
                value={parseValue(value)}
                onChange={(v) => {
                  if (data && rescheduler) rescheduler.removeSavedSchedule(data);
                  if (!v || !startsAt || !endsAt) return;
                  setStartAndEndDateValue(v);
                }}
                mode="single"
                className="w-[180px]"
                disabled={disabled}
                placeholder={t('default.select.placeholder')}
                noRemove
                {...field}
              />
            </FormItemWrapper>
          )}
        />
        <FormField
          control={control}
          name="startsAt"
          render={({ field: { value, onChange, ...field } }) => (
            <FormItemWrapper className="w-fit" label={t('startTime.label')} required>
              <TimeInput
                {...field}
                asInput
                internal
                id={field.name}
                value={parseValue(value)}
                disabled={disabled}
                onValueChange={(v) => {
                  if (data && rescheduler) rescheduler.removeSavedSchedule(data);
                  if (!v || !startsAt) return;
                  setValue(
                    'startsAt',
                    set(toTenantDate(startsAt), {
                      hours: getHours(v),
                      minutes: getMinutes(v),
                    }).toISOString(),
                  );
                }}
              />
            </FormItemWrapper>
          )}
        />
        <FormField
          control={control}
          name="endsAt"
          render={({ field: { value, onChange, ...field } }) => (
            <FormItemWrapper className="w-fit" label={t('endTime.label')} required>
              <TimeInput
                {...field}
                asInput
                internal
                id={field.name}
                value={parseValue(value)}
                disabled={disabled}
                onValueChange={(v) => {
                  if (data && rescheduler) rescheduler.removeSavedSchedule(data);
                  if (!v || !endsAt) return;
                  setValue(
                    'endsAt',
                    set(toTenantDate(endsAt), {
                      hours: getHours(v),
                      minutes: getMinutes(v),
                    }).toISOString(),
                  );
                }}
              />
            </FormItemWrapper>
          )}
        />
        {children}
      </div>
      <SchedulerCompanyTime />
    </div>
  );
};
